import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

const services = [
  {
    title: "Film & Animation",
    body: "Short films, title sequences, music videos, animated vignettes.",
    tag: "Cinema",
  },
  {
    title: "Brand Identity",
    body: "Wordmarks, logo systems, editorial palettes for labels and studios.",
    tag: "Design",
  },
  {
    title: "Story Development",
    body: "Script notes, narrative consultation, treatment writing.",
    tag: "Writing",
  },
  {
    title: "Live Event Coverage",
    body: "Cinematic capture of raves, deep dubstep nights, house music sets, concerts, festivals, brand activations, and marketing experiences — shot for low light, smoke, strobe, and the energy that lives between the drops.",
    tag: "On Location",
  },
];

export default function Projects() {
  return (
    <div className="page-enter max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28" data-testid="projects-page">
      <div className="overline">The Vault</div>
      <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl mt-4" data-testid="projects-heading">
        The vault is still being <em style={{ color: "var(--gl-amber)" }}>dug</em>.
      </h1>
      <p className="mt-6 max-w-xl" style={{ color: "var(--gl-muted)" }}>
        GraveLight Studio is newly opened. Our first case studies are in the soil — films, brand
        systems, and live event coverage that we'll surface here once the earth has settled.
      </p>

      {/* Empty state card */}
      <div
        className="mt-12 gl-card p-10 md:p-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-8"
        data-testid="projects-empty-state"
      >
        <div className="flex items-center gap-6">
          {/* small tombstone glyph */}
          <svg width="72" height="90" viewBox="0 0 72 90" aria-hidden>
            <defs>
              <radialGradient id="pg" cx="80%" cy="30%" r="60%">
                <stop offset="0%" stopColor="rgba(251,191,36,0.35)" />
                <stop offset="100%" stopColor="rgba(251,191,36,0)" />
              </radialGradient>
            </defs>
            <rect x="0" y="0" width="72" height="90" fill="url(#pg)" />
            <path
              d="M 12 84 L 12 34 A 24 24 0 0 1 60 34 L 60 84 Z"
              fill="#1a1d23"
              stroke="rgba(251,191,36,0.5)"
              strokeWidth="1"
            />
            <text
              x="36"
              y="52"
              textAnchor="middle"
              fontFamily="'Cormorant Garamond', serif"
              fontSize="10"
              fill="#c9b278"
              letterSpacing="1.2"
            >
              SOON
            </text>
            <line x1="20" y1="60" x2="52" y2="60" stroke="#c9b278" opacity="0.55" />
            <text
              x="36"
              y="72"
              textAnchor="middle"
              fontFamily="'Cormorant Garamond', serif"
              fontStyle="italic"
              fontSize="8"
              fill="#8c7f5f"
            >
              ··· ··· ···
            </text>
          </svg>
          <div>
            <div className="overline mb-1">Status</div>
            <div className="font-display text-2xl">No work buried here — yet.</div>
            <p className="text-sm mt-2 max-w-md" style={{ color: "var(--gl-muted)" }}>
              Have a project that should be our first headstone in this vault? We're taking on a
              small number of founding commissions.
            </p>
          </div>
        </div>
        <Link to="/contact" className="btn-amber shrink-0" data-testid="projects-empty-cta">
          Be our first commission
        </Link>
      </div>

      {/* Services we offer */}
      <div className="mt-24">
        <div className="overline">What we offer</div>
        <h2 className="font-display text-4xl mt-3 mb-10">Services</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {services.map((s, i) => (
            <motion.article
              key={s.title}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.6, delay: i * 0.06 }}
              className="gl-card p-8"
              data-testid={`projects-service-${i}`}
            >
              <div className="overline mb-2">{s.tag}</div>
              <div className="font-display text-3xl">{s.title}</div>
              <p className="text-sm mt-3" style={{ color: "var(--gl-muted)" }}>{s.body}</p>
              <div className="mt-6">
                <Link to="/contact" className="gl-link text-xs tracking-[0.25em] uppercase" data-testid={`projects-service-cta-${i}`}>
                  Enquire →
                </Link>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </div>
  );
}
